/**
 * PATH: packages/email-hub/src/feature/components/sender-identity/CrmIdentity.tsx
 * FILE_ID: EMAILHUB-SENDERIDENTITY-CRM-001
 * PURPOSE: Render the customer-CRM identity states — resolved sender, and the
 *          unknown sender with its one-click Add-to-CRM nudge.
 * ROLE IN ARCHITECTURE: Presentational leaf of the sender-identity module.
 * MAIN EXPORTS: ResolvedIdentity, UnknownCrmIdentity.
 * NON-RESPONSIBILITIES: No gating (predicates.ts), no mutations (actions hook).
 *
 * NOTES FOR FUTURE AI: "Wrong person?" is not a nicety. A wrong CRM match used
 * to be permanent in the UI — the operator could see the wrong name on an email
 * and had no way to correct it (BUG-2026-07-09-INTERNAL-SENDER-CRM). It severs
 * only the address→Contact link, so the fix is reversible by re-filing. The
 * ``flat`` variant drops the border/fill because inside a reader lane a bordered
 * pill reads as a card nested in a card (operator 2026-07-25: "so cluttered").
 * Moved verbatim out of SenderIdentityCard.tsx (change #302, size split).
 */
import { Building2, CheckCircle2, Loader2, ShieldQuestion, User, UserPlus, XCircle } from 'lucide-react'

import { cn } from '../../../_internal/lib/utils'
import type { SenderIdentityActions } from './useSenderIdentityActions'

interface ResolvedIdentityProps {
  contactName: string | null
  clientName: string | null
  actions: SenderIdentityActions
  /** Flat chip (reader lane) vs bordered pill (standalone reader). */
  flat: boolean
  pad: string
  className?: string
}

export function ResolvedIdentity({
  contactName,
  clientName,
  actions,
  flat,
  pad,
  className,
}: ResolvedIdentityProps) {
  const name = contactName || clientName
  const glyph = contactName ? User : Building2
  const Glyph = glyph

  if (flat) {
    return (
      <div
        className="flex items-center gap-1.5 min-w-0 text-emerald-700 dark:text-emerald-300"
        data-testid="sender-identity-card"
        data-resolved="true"
      >
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="truncate font-medium">{name || 'Known sender'}</span>
        {contactName && clientName && (
          <span className="truncate text-[10.5px] opacity-70">{clientName}</span>
        )}
        <button
          type="button"
          onClick={actions.detachWrongPerson}
          disabled={actions.busy}
          title="Wrong person? Remove this address from the matched CRM contact"
          data-testid="sender-wrong-person"
          className="shrink-0 inline-flex items-center justify-center rounded-md h-[18px] w-[18px] text-emerald-700/60 dark:text-emerald-300/60 hover:text-emerald-900 dark:hover:text-emerald-100 hover:bg-emerald-100/60 dark:hover:bg-emerald-900/40 disabled:opacity-50 transition-colors"
        >
          {actions.busy ? (
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          ) : (
            <ShieldQuestion className="h-3 w-3" aria-hidden />
          )}
        </button>
        {actions.error && (
          <span className="text-destructive text-[10.5px] shrink-0">{actions.error}</span>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex items-center gap-2 flex-wrap rounded-lg border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-900/20',
        pad,
        className,
      )}
      data-testid="sender-identity-card"
      data-resolved="true"
    >
      <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" aria-hidden />
      <span className="text-emerald-800 dark:text-emerald-200 font-medium">Known sender</span>
      {contactName && (
        <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300">
          <User className="h-3.5 w-3.5 shrink-0" aria-hidden /> {contactName}
        </span>
      )}
      {clientName && (
        <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300">
          <Glyph className="h-3.5 w-3.5 shrink-0" aria-hidden /> {clientName}
        </span>
      )}
      {/* Inline dispute: a wrong CRM match was previously permanent —
          the operator could see "John Smith" on Rebecca's email and do
          nothing about it. Two clicks now fix the data. */}
      <button
        type="button"
        onClick={actions.detachWrongPerson}
        disabled={actions.busy}
        title="Wrong person? Remove this address from the matched CRM contact"
        data-testid="sender-wrong-person"
        className="ml-auto inline-flex items-center gap-1 rounded-md border border-emerald-300/60 dark:border-emerald-700/60 px-1.5 py-0.5 text-[10px] text-emerald-700/70 dark:text-emerald-300/70 hover:text-emerald-900 hover:bg-emerald-100/60 dark:hover:bg-emerald-900/40 disabled:opacity-60 transition-colors"
      >
        {actions.busy ? (
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        ) : (
          <ShieldQuestion className="h-3 w-3" aria-hidden />
        )}
        Wrong person?
      </button>
      {actions.error && (
        <span className="w-full text-xs text-red-600 dark:text-red-400">{actions.error}</span>
      )}
    </div>
  )
}

interface UnknownCrmIdentityProps {
  senderLabel: string
  actions: SenderIdentityActions
  flat: boolean
  pad: string
  className?: string
}

export function UnknownCrmIdentity({
  senderLabel,
  actions,
  flat,
  pad,
  className,
}: UnknownCrmIdentityProps) {
  if (flat) {
    return (
      <div
        className="flex items-center gap-1.5 min-w-0"
        data-testid="sender-identity-card"
        data-resolved="false"
      >
        <XCircle className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
        <span className="truncate font-medium text-foreground/90">{senderLabel}</span>
        <button
          type="button"
          onClick={actions.addToCrm}
          disabled={actions.busy}
          title={`${senderLabel} isn’t in your CRM — file them as a contact`}
          data-testid="sender-add-to-crm"
          className="shrink-0 inline-flex items-center gap-1 rounded-md bg-amber-600 px-2 py-0.5 text-[10.5px] font-medium text-white hover:bg-amber-700 disabled:opacity-60 transition-colors"
        >
          {actions.busy ? (
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          ) : (
            <UserPlus className="h-3 w-3" aria-hidden />
          )}
          {actions.busy ? 'Adding…' : 'Add to CRM'}
        </button>
        {actions.error && (
          <span className="text-destructive text-[10.5px] shrink-0">{actions.error}</span>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex items-center gap-2 flex-wrap rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20',
        pad,
        className,
      )}
      data-testid="sender-identity-card"
      data-resolved="false"
    >
      <XCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" aria-hidden />
      <span className="text-amber-800 dark:text-amber-200">
        <span className="font-medium">{senderLabel}</span> isn’t in your CRM
      </span>
      <button
        type="button"
        onClick={actions.addToCrm}
        disabled={actions.busy}
        data-testid="sender-add-to-crm"
        className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-60 transition-colors"
      >
        {actions.busy ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        ) : (
          <UserPlus className="h-3.5 w-3.5" aria-hidden />
        )}
        {actions.busy ? 'Adding…' : 'Add to CRM'}
      </button>
      {actions.error && (
        <span className="w-full text-xs text-red-600 dark:text-red-400">{actions.error}</span>
      )}
    </div>
  )
}
